import {Image} from '@shopify/hydrogen';
import clsx from 'clsx';
import React, {ReactElement, ReactNode} from 'react';

const DEFAULT_CLASSES =
  'relative font-black !font-normal block m-0 w-full flex items-center justify-center text-center px-12 py-4 disabled:border-gray-300 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-300 hover:bg-[#FFFFFF] hover:text-acai-900';

const VARIANT_CLASSES = {
  primary: `text-white border border-acai-900 bg-acai-900 hover:bg-white hover:border-white active:!border-daybreak-400 active:!bg-daybreak-400 active:!text-white`,
  secondary: `bg-white hover:bg-gray-50 active:bg-gray-100 border border-acai-900`,
  outline: `bg-transparent border border-acai-900 hover:bg-white hover:text-acai-900 active:!bg-daybreak-400 active:!text-white text-acai-900`,
};

export const BUTTON_PRIMARY_CLASSES = `${DEFAULT_CLASSES} ${VARIANT_CLASSES.primary}`;
export const BUTTON_SECONDARY_CLASSES = `${DEFAULT_CLASSES} ${VARIANT_CLASSES.secondary}`;

const ExternalIcon = () => (
  <svg
    className="ml-3 fill-current text-white"
    width="15"
    height="14"
    viewBox="0 0 15 14"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M8.11963 0.000976562C7.56734 0.000976562 7.11963 0.448692 7.11963 1.00098C7.11963 1.55326 7.56734 2.00098 8.11963 2.00098H10.7054L4.41252 8.29387C4.022 8.68439 4.022 9.31756 4.41252 9.70808C4.80305 10.0986 5.43621 10.0986 5.82674 9.70808L12.1196 3.41519V6.00098C12.1196 6.55326 12.5673 7.00098 13.1196 7.00098C13.6719 7.00098 14.1196 6.55326 14.1196 6.00098V1.00098C14.1196 0.448692 13.6719 0.000976562 13.1196 0.000976562H8.11963Z" />
    <path d="M2.11963 2.00098C1.01506 2.00098 0.119629 2.89641 0.119629 4.00098V12.001C0.119629 13.1055 1.01506 14.001 2.11963 14.001H10.1196C11.2242 14.001 12.1196 13.1055 12.1196 12.001V9.00098C12.1196 8.44869 11.6719 8.00098 11.1196 8.00098C10.5673 8.00098 10.1196 8.44869 10.1196 9.00098V12.001H2.11963V4.00098L5.11963 4.00098C5.67191 4.00098 6.11963 3.55326 6.11963 3.00098C6.11963 2.44869 5.67191 2.00098 5.11963 2.00098H2.11963Z" />
  </svg>
);

interface ButtonProps {
  className?: string;
  label?: string;
  handleClick?: React.MouseEventHandler<HTMLButtonElement>;
  url?: string;
  type?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  passthroughProps?: any;
  loading?: boolean;
  disabled?: boolean;
  rounded?: boolean;
  variant?: 'primary' | 'secondary' | 'outline';
  children?: ReactNode;
  externalIcon?: boolean;
  loadingIcon?: 'tfr' | 'spiner';
}

/**
 * A client component that sets the primary and secondary classes for button components
 */
export function Button({
  className,
  label,
  handleClick,
  url,
  variant = 'primary',
  passthroughProps,
  type,
  rounded = true,
  loading = false,
  disabled = false,
  children,
  externalIcon = true,
  loadingIcon = 'tfr',
}: ButtonProps): ReactElement {
  const classes = clsx(
    DEFAULT_CLASSES,
    VARIANT_CLASSES[variant],
    className,
    rounded && 'rounded-[36px]',
    loading && 'cursor-not-allowed',
    disabled && 'cursor-not-allowed hover:cursor-not-allowed brightness-75',
  );
  const isExternal = url
    ? url.indexOf('://') > 0 || url.indexOf('//') === 0
    : false;

  if (isExternal) {
    return (
      <a href={url} className={classes} {...passthroughProps}>
        {label || children}
        {externalIcon && <ExternalIcon />}
      </a>
    );
  }

  if (type) {
    return (
      <button className={classes} type="submit">
        {loading && (
          <div className="transfrom absolute left-[5%] top-1/2 -translate-y-1/2">
            {loadingIcon === 'spiner' && (
              <div className="h-5 w-5 animate-spin rounded-full border-b-[3px] border-t-[3px] border-gray-500 text-foam-100" />
            )}
            {loadingIcon === 'tfr' && (
              <Image
                src="/tfr-loop.gif"
                alt="tfr-loop"
                width={30}
                height={30}
              />
            )}
          </div>
        )}
        {label ?? children}
      </button>
    );
  }

  if (handleClick) {
    return (
      <button className={classes} onClick={handleClick} type="button">
        {loading && (
          <div className="transfrom absolute left-[5%] top-1/2 -translate-y-1/2">
            {loadingIcon === 'spiner' && (
              <div className="h-5 w-5 animate-spin rounded-full border-b-[3px] border-t-[3px] border-gray-500 text-foam-100" />
            )}
            {loadingIcon === 'tfr' && (
              <Image
                src="/tfr-loop.gif"
                alt="tfr-loop"
                width={30}
                height={30}
              />
            )}
          </div>
        )}
        {label ?? children}
      </button>
    );
  }

  return (
    <a href={url} className={classes} {...passthroughProps}>
      {loading && (
        <div className="transfrom absolute left-[5%] top-1/2 -translate-y-1/2">
          {loadingIcon === 'spiner' && (
            <div className="h-5 w-5 animate-spin rounded-full border-b-[3px] border-t-[3px] border-gray-500 text-foam-100" />
          )}
          {loadingIcon === 'tfr' && (
            <Image src="/tfr-loop.gif" alt="tfr-loop" width={30} height={30} />
          )}
        </div>
      )}
      {label ?? children}
    </a>
  );
}
